/**
 * GalleryImageGrid — 드래그앤드롭 정렬 가능한 갤러리 이미지 그리드
 */

import { useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, X } from 'lucide-react'
import styles from './SectionForm.module.css'

interface GalleryImageGridProps {
  images: string[]
  onReorder: (images: string[]) => void
  onDelete: (index: number) => void
}

function SortableImage({
  url,
  index,
  onDelete,
}: {
  url: string
  index: number
  onDelete: (i: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: url })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : ('auto' as const),
  }

  return (
    <div ref={setNodeRef} style={style} className={styles.galleryItem}>
      <img src={url} alt={`갤러리 ${index + 1}`} className={styles.galleryItemImg} />
      <button
        type="button"
        className={styles.galleryItemDelete}
        onClick={() => onDelete(index)}
        aria-label="삭제"
      >
        <X size={14} />
      </button>
      <button
        type="button"
        className={styles.galleryItemDrag}
        {...attributes}
        {...listeners}
        aria-label="드래그"
      >
        <GripVertical size={14} />
      </button>
    </div>
  )
}

export function GalleryImageGrid({ images, onReorder, onDelete }: GalleryImageGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = images.indexOf(active.id as string)
      const newIndex = images.indexOf(over.id as string)
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(images, oldIndex, newIndex))
      }
    },
    [images, onReorder]
  )

  if (images.length === 0) return null

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images} strategy={rectSortingStrategy}>
        <div className={styles.galleryGrid}>
          {images.map((url, i) => (
            <SortableImage key={url} url={url} index={i} onDelete={onDelete} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
